import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search, RefreshCw, Users, AlertCircle, Plus,
  X, LayoutGrid, Table2, ChevronRight, MapPinned, Eye, FileDown,
  ShieldCheck, Loader2, FileSpreadsheet
} from 'lucide-react';

import { fetchAllEmployees, fetchEmployeeByServiceNo, setSelectedEmployee } from '../../store/slices/employeeSlice';
import { setModalOpen, setSearchQuery, clearError } from '../../store/slices/employeeSlice';
import EmployeeCard   from '../../components/employee/EmployeeCard';
import EmployeeModal  from '../../components/employee/EmployeeModal';
import AttendanceModal from '../../components/employee/AttendanceModal';
import PhysicalLocationModal from '../../components/employee/PhysicalLocationModal';
import { fetchPhysicalLocations } from '../../store/slices/physicalLocationSlice';
import { fetchLocationMaster } from '../../store/slices/locationMasterSlice';
import { buildOptions } from '../../utils/dropdownOpts';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import AddEmployeeForm from '../../components/employee/AddEmployeeForm';
import EmployeeEditModal from '../../pages/employee/Employeeeditmodal';
import ImportEmployeesModal from '../../components/employee/Importemployeesmodal';
// isActive reads HED_IS_ACTIVE as true/false, 1/0 or "True"/"False" (Boolean("False") is true).
import { exportEmployeesToExcel, isActive as isEmployeeActive } from '../../utils/employeeExcel';
import {
  getProvinceList, getDistrictList, getElectorateList, getPoliceStationList, getGramaList,
} from '../../store/slices/regionSlice';
import { getLocationName } from '../../utils/employeeUtils';
import employeeService from '../../services/EmployeeService';
import {
  isApproved, isPendingApproval, countPendingApproval,
  buildApprovalPayload, buildRejectionPayload,
} from '../../utils/employeeApproval';
import { PENDING_OUTLINE_BTN } from '../../constants/pendingTheme';
import { buildBankApprovalPayload } from '../../utils/employeeBankApproval';
import tenant from '../../config/tenant';

const fmt = (v) => (!v || v === 'null') ? '' : String(v).trim();
const fmtDate = (v) => {
  if (!v || v === 'null') return '—';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return String(v); }
};

const fld = (emp, newKey, oldKey) => fmt(emp[newKey]) || fmt(emp[oldKey]);

const employeeAccent = tenant.theme.accent?.employee || tenant.theme.primary[500];
const employeeText = tenant.theme.accent?.empText || tenant.theme.primary[700];

// ─── Table Row ────────────────────────────────────────────────────────────────
function TableRow({ emp, onClick, maps, idx, busy }) {
  const pending = isPendingApproval(emp);
  const isActive = isEmployeeActive(emp);

  const name     = fld(emp, 'HED_REPORT_NAME', 'p_report_name') ||
                   `${fld(emp, 'HED_FIRST_NAME', 'p_first_name')} ${fld(emp, 'HED_LAST_NAME', 'p_last_name')}`.trim();
  const svcNo    = fld(emp, 'HED_SERVICE_NO', 'p_ced_service_no');
  const empId    = emp.HED_EMPLOYEE_ID;
  const clickId  = empId || (emp.p_ced_service_no ? String(emp.p_ced_service_no).trim() : null);

  const dept     = fld(emp, 'HDD_DEPARTMENT_NAME', 'p_department_code') || '—';
  const loc      = getLocationName(emp) || '—';
  const desig    = fld(emp, 'HED_DESIGNATION_NAME', 'p_designation') || '—';
  const category = fld(emp, 'HEC_CATEGORY_NAME', 'p_cat_code') || '—';
  const joinDate = fld(emp, 'HED_DATE_OF_JOINING', 'p_recruitment_date');

  return (
    <tr
      onClick={() => clickId && onClick(clickId)}
      className={`cursor-pointer border-b border-gray-100 transition-colors hover:bg-blue-50/60
        ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
    >
      <td className="px-3 py-2 text-[12px] font-mono font-semibold text-blue-700 whitespace-nowrap">{svcNo || '—'}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <div>
            <p className="text-[12px] font-semibold text-gray-900 whitespace-nowrap">{name || '—'}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`inline-flex items-center gap-1 text-[10px] font-medium
                ${isActive ? 'text-emerald-600' : 'text-gray-400'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                {isActive ? 'Active' : 'Inactive'}
              </span>
              {pending && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                  Pending Approval
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-[12px] text-gray-700 whitespace-nowrap">{desig}</td>
      <td className="px-3 py-2 text-[12px] text-gray-700 whitespace-nowrap">{category}</td>
      <td className="px-3 py-2 text-[12px] text-gray-700 whitespace-nowrap max-w-[160px] truncate" title={loc}>{loc}</td>
      <td className="px-3 py-2 text-[12px] text-gray-700 whitespace-nowrap max-w-[180px] truncate" title={dept}>{dept}</td>
      <td className="px-3 py-2 text-[12px] text-gray-500 whitespace-nowrap">{fmtDate(joinDate)}</td>
      <td className="px-3 py-2 text-center whitespace-nowrap">
        <div className="inline-flex items-center gap-1.5">
          {/* The approve/reject decision is made inside the detail modal, so the
              row just opens the record — worded to say a decision is waiting. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (clickId) onClick(clickId);
            }}
            title={pending ? 'Open to approve or reject' : 'View Profile Details'}
            disabled={busy}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed
              ${pending
                ? PENDING_OUTLINE_BTN
                : 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-600 hover:text-white hover:border-blue-600'}`}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
            <span>{pending ? 'Review' : 'View'}</span>
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Table View ───────────────────────────────────────────────────────────────
function TableView({ employees, onRowClick, maps, approvingId }) {
  const [collapsed, setCollapsed] = useState({});

  const toggle = (key) => setCollapsed(p => ({ ...p, [key]: !p[key] }));

  // Group by department
  const groups = useMemo(() => {
    const g = {};
    employees.forEach(emp => {
      const deptName = fld(emp, 'HDD_DEPARTMENT_NAME', 'p_department_code') || 'Unassigned';
      const deptCode = fld(emp, 'HDD_DEPARTMENT_CODE', 'p_department_code') || 'unassigned';
      if (!g[deptCode]) g[deptCode] = { label: deptName, code: deptCode, rows: [] };
      g[deptCode].rows.push(emp);
    });
    return Object.values(g).sort((a, b) => a.label.localeCompare(b.label));
  }, [employees]);

  const COLS = [
    'Service No', 'Report Name', 'Designation',
    'Category', 'Location / Cost Center', 'Department', 'Join Date', 'Actions',
  ];

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm bg-white">
      <table className="w-full min-w-[900px] border-collapse">
        <thead>
          <tr className="bg-slate-800 text-white">
            {COLS.map(col => (
              <th key={col} className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(group => {
            const isCollapsed = collapsed[group.code];
            return (
              <React.Fragment key={group.code}>
                {/* Group header row */}
                <tr
                  className="bg-blue-50 border-y border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => toggle(group.code)}
                >
                  <td colSpan={COLS.length} className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <ChevronRight
                        size={14}
                        className={`text-blue-600 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                      />
                      <span className="text-[12px] font-bold text-blue-800">
                        {group.label}
                      </span>
                      <span className="text-[11px] text-blue-500 font-medium">
                        ({group.rows.length} employee{group.rows.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                  </td>
                </tr>
                {/* Employee rows */}
                {!isCollapsed && group.rows.map((emp, idx) => (
                  <TableRow
                    key={emp.HED_EMPLOYEE_ID || emp.p_ced_service_no || idx}
                    emp={emp}
                    onClick={onRowClick}
                    maps={maps}
                    idx={idx}
                    busy={approvingId === (emp.HED_EMPLOYEE_ID || fld(emp, 'HED_SERVICE_NO', 'p_ced_service_no'))}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function EmployeeManagement() {
  const dispatch = useDispatch();
  const { employees, loading, error, modalOpen, searchQuery, selectedEmployee, detailLoading } =
    useSelector((s) => s.employees);
  const dropdowns = useSelector((s) => s.dropdowns || {});
  const { user } = useSelector((s) => s.auth || {});

  const [showAddForm, setShowAddForm] = useState(false);
  const [showImport, setShowImport]   = useState(false);
  const [deptFilter, setDeptFilter]   = useState('');
  const [viewMode, setViewMode]       = useState('card');
  const [attendanceFor, setAttendanceFor] = useState(null); 
  const [physicalLocationFor, setPhysicalLocationFor] = useState(null);
  const [tab, setTab]                     = useState('employees'); // 'employees' | 'approvals'
  // Employees tab: Active by default, switch to see Inactive.
  const [statusView, setStatusView]       = useState('active');   // 'active' | 'inactive'
  const [approvingId, setApprovingId]     = useState(null);
  const [approveError, setApproveError]   = useState(null);
  const [locFilter, setLocFilter]         = useState('');
  const [showLocDD, setShowLocDD]         = useState(false);
  const [locFilterLoading, setLocFilterLoading] = useState(false);
  const physicalLocationState = useSelector(s => s.physicalLocation);
  const locationMasterState = useSelector(s => s.locationMaster);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState(null);


  const handleSaveEmployee = async (updated) => {
    setSaving(true);
    setSaveError(null);
    try {
      dispatch(fetchAllEmployees());
      const targetId = updated?.HED_EMPLOYEE_ID || updated?.HED_SERVICE_NO || updated?.p_ced_service_no || selectedEmployee?.HED_EMPLOYEE_ID || selectedEmployee?.HED_SERVICE_NO || selectedEmployee?.p_ced_service_no;
      if (targetId) {
        const res = await dispatch(fetchEmployeeByServiceNo(targetId)).unwrap();
        if (res) {
          dispatch(setSelectedEmployee(res));
        }
      } else if (updated) {
        dispatch(setSelectedEmployee({ ...selectedEmployee, ...updated }));
      }
      setEditing(false);
    } catch (err) {
      console.error('[handleSaveEmployee] error:', err);
      const msg = typeof err === 'string' ? err : (err?.message || 'API error occurred while saving changes.');
      setSaveError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { dispatch(fetchAllEmployees()); }, [dispatch]);
  useEffect(() => {
    if (!locationMasterState.loaded && !locationMasterState.loading) dispatch(fetchLocationMaster());
  }, []);

  const maps = useMemo(() => {
    const bm = (arr, kc, kn) => {
      const m = {};
      (arr || []).forEach(i => { if (i[kc] != null) m[String(i[kc])] = String(i[kn] ?? i[kc]); });
      return m;
    };
    return {
      empCat: bm(dropdowns.empCat,     'CategoryCode',   'CategoryName'),
      loc:    bm(dropdowns.locDetails, 'p_hld_loc_code',      'p_hld_loc_desc'),
      dep:    bm(dropdowns.depDetails, 'p_hdd_dep_code',      'p_hdd_dep_desc'),
      dev:    bm(dropdowns.devDetails, 'DivisionCode',   'DivisionName'),
      shift:  bm(dropdowns.shiftTypes, 'ShiftCode',      'ShiftName'),
    };
  }, [dropdowns]);

  const departments = useMemo(() => {
    const seen = new Map();
    employees.forEach(e => {
      const code = fld(e, 'HDD_DEPARTMENT_CODE', 'p_department_code');
      const name = fld(e, 'HDD_DEPARTMENT_NAME', 'p_department_code');
      if (code && !seen.has(code)) seen.set(code, name || code);
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([code, label]) => ({ code, label }));
  }, [employees]);

  const filtered = useMemo(() => {
    const q = (searchQuery || '').toLowerCase().trim();
    return employees.filter(emp => {
      const deptCode = fld(emp, 'HDD_DEPARTMENT_CODE', 'p_department_code');
      const matchesDept = !deptFilter || deptCode === deptFilter;
      if (!matchesDept) return false;
      if (!q) return true;
      const name   = `${fld(emp, 'HED_REPORT_NAME', 'p_report_name')} ${fld(emp, 'HED_FIRST_NAME', 'p_first_name')} ${fld(emp, 'HED_LAST_NAME', 'p_last_name')}`.toLowerCase();
      const svcNo  = fld(emp, 'HED_SERVICE_NO', 'p_ced_service_no').toLowerCase();
      const desig  = fld(emp, 'HED_DESIGNATION_NAME', 'p_designation').toLowerCase();
      const mobile = fld(emp, 'HED_MOBILE_NO', 'p_mobile').toLowerCase();
      const email  = fld(emp, 'HED_COMPANY_EMAIL', 'p_email_address').toLowerCase();
      const nic    = fld(emp, 'HED_NIC_NO', 'p_nid').toLowerCase();
      return name.includes(q) || svcNo.includes(q) || desig.includes(q) || mobile.includes(q) || email.includes(q) || nic.includes(q);
    });
  }, [employees, searchQuery, deptFilter]);

  const activeCount = employees.filter(isEmployeeActive).length;

  // Approved employees, split Active / Inactive — counts for the switch.
  const approvedEmployees = useMemo(() => filtered.filter(isApproved), [filtered]);
  const approvedActiveCount = approvedEmployees.filter(isEmployeeActive).length;
  const approvedInactiveCount = approvedEmployees.length - approvedActiveCount;

  const locOptions = useMemo(
    () => (locationMasterState.rows || [])
      .map(r => ({ value: String(r.HLD_LOC_CODE ?? '').trim(), label: String(r.HLD_LOC_DESC ?? r.HLD_LOC_CODE ?? '').trim() }))
      .filter(o => o.value)
      .filter(o => o.value.toUpperCase().includes('PHY')),
    [locationMasterState.rows]
  );
  const phyLocNameFor = (code) => (locOptions.find(o => o.value === code)?.label) || code;

  const runLocationFilter = async (locCode) => {
    setLocFilter(locCode);
    setShowLocDD(false);
    if (!locCode) return;
    setLocFilterLoading(true);
    const targets = [...new Set(filtered.map(e => fld(e, 'HED_SERVICE_NO', 'p_ced_service_no')).filter(Boolean))];
    const toFetch = targets.filter(sn => !(sn in physicalLocationState.cache));
    await Promise.allSettled(toFetch.map(sn => dispatch(fetchPhysicalLocations({ serviceNo: sn }))));
    setLocFilterLoading(false);
  };

  const visibleEmployees = useMemo(() => {
    let rowsOut = filtered;
    if (locFilter) {
      rowsOut = rowsOut.filter(emp => {
        const sn = fld(emp, 'HED_SERVICE_NO', 'p_ced_service_no');
        const rows = physicalLocationState.cache[sn] || [];
        return rows.some(r => String(r.p_loc_code ?? '').trim() === locFilter);
      });
    }
    // Employees tab = approved only (HED_IS_APPROVED 1).
    // Approvals tab = pending only  (HED_IS_APPROVED 0 / null).
    rowsOut = rowsOut.filter(tab === 'approvals' ? isPendingApproval : isApproved);
    // Employees tab shows Active or Inactive employees, per the switch.
    if (tab === 'employees') {
      rowsOut = rowsOut.filter(emp => (statusView === 'active' ? isEmployeeActive(emp) : !isEmployeeActive(emp)));
    }
    return rowsOut;
  }, [filtered, locFilter, physicalLocationState.cache, tab, statusView]);

  // Count across ALL employees, not the filtered view — the badge is a global
  // "how much is waiting on me" figure.
  const pendingCount = useMemo(() => countPendingApproval(employees), [employees]);

  // Approve (p_isApproved 1) and Reject (p_isApproved 2) differ only in payload.
  const decide = async (emp, kind) => {
    const empKey = emp.HED_EMPLOYEE_ID || fld(emp, 'HED_SERVICE_NO', 'p_ced_service_no');
    const decidedBy = user?.serviceNo;
    if (!decidedBy) {
      setApproveError('Could not identify the logged-in user. Please sign in again.');
      return false;
    }
    setApproveError(null);
    setApprovingId(empKey);
    try {
      const payload = kind === 'reject'
        ? buildRejectionPayload(emp, decidedBy)
        : buildApprovalPayload(emp, decidedBy);
      await employeeService.UpdateEmployee(payload);
      await dispatch(fetchAllEmployees());
      return true;
    } catch (err) {
      setApproveError(err?.message || `Could not ${kind} this employee. Please try again.`);
      return false;
    } finally {
      setApprovingId(null);
    }
  };

  /**
   * Approve the ticked bank accounts. Untick means untouched — an account that
   * wasn't ticked never reaches EMP/UpdateEmployeeBank.
   * Returns true when every call succeeded (or there was nothing to send).
   */
  const approveBanks = async (banks, approvedBy) => {
    if (!banks || banks.length === 0) return true;
    const results = await Promise.allSettled(
      banks.map((b) => employeeService.UpdateEmployeeBank(buildBankApprovalPayload(b, approvedBy)))
    );
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      setApproveError(
        failed.length === banks.length
          ? 'Could not approve the bank details. Please try again.'
          : `Approved ${banks.length - failed.length} of ${banks.length} bank accounts. Please retry the rest.`
      );
      return false;
    }
    return true;
  };

  // Approve/Reject are triggered from inside the detail modal. On success the
  // modal closes — the record has left the Approvals tab, so keeping it open
  // would show a decision that no longer applies.
  //
  // The modal passes the bank accounts the reviewer ticked; the employee record
  // itself is only sent when it is still pending, so approving a bank account on
  // an already-approved employee does not re-stamp the employee.
  const handleApprove = async (emp, tickedBanks = []) => {
    const approvedBy = user?.serviceNo;
    if (!approvedBy) {
      setApproveError('Could not identify the logged-in user. Please sign in again.');
      return;
    }

    const empKey = emp.HED_EMPLOYEE_ID || fld(emp, 'HED_SERVICE_NO', 'p_ced_service_no');
    const needsEmployee = isPendingApproval(emp);

    // Banks first, so a bank failure doesn't leave the employee approved
    // while the account it was approved alongside silently stayed pending.
    setApproveError(null);
    setApprovingId(empKey);
    let banksOk = true;
    try {
      banksOk = await approveBanks(tickedBanks, approvedBy);
    } finally {
      setApprovingId(null);
    }
    if (!banksOk) return;

    if (!needsEmployee) {
      await dispatch(fetchAllEmployees());
      dispatch(setModalOpen(false));
      return;
    }

    const ok = await decide(emp, 'approve');
    if (ok) dispatch(setModalOpen(false));
  };
  const handleReject = async (emp) => {
    const ok = await decide(emp, 'reject');
    if (ok) dispatch(setModalOpen(false));
  };

  const handleEmployeeClick = (idOrSvcNo) => {
    dispatch(fetchEmployeeByServiceNo(idOrSvcNo));
  };

  // Exports exactly what's on screen — same list the table/cards render.
  // Region lists are loaded first so the sheet shows names, not IDs.
  const handleExportExcel = async () => {
    if (!visibleEmployees.length) return;
    const load = (thunk) => dispatch(thunk()).unwrap().catch(() => []);
    const [provinces, districts, electorates, policeStations, gramas] = await Promise.all([
      load(getProvinceList), load(getDistrictList), load(getElectorateList),
      load(getPoliceStationList), load(getGramaList),
    ]);
    exportEmployeesToExcel({
      employees: visibleEmployees,
      provinces, districts, electorates, policeStations, gramas,
      filters: {
        Search: searchQuery || '',
        Department: deptFilter ? (departments.find(d => d.code === deptFilter)?.label || deptFilter) : '',
        'Physical Location': locFilter ? phyLocNameFor(locFilter) : '',
      },
    });
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-600 rounded-xl flex items-center justify-center shadow-sm">
              <Users size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Employee Management</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {loading
                  ? 'Loading employees...'
                  : tab === 'approvals'
                    ? `${visibleEmployees.length} awaiting approval`
                    : `${visibleEmployees.length} ${statusView === 'active' ? 'active' : 'inactive'} of ${employees.length} employees · ${activeCount} active`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">

            {/* Search */}
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchQuery}
                onChange={e => dispatch(setSearchQuery(e.target.value))}
                placeholder="Search name, ID, NIC, mobile..."
                className="pl-8 pr-8 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 w-56 bg-gray-50 focus:bg-white transition-colors"
              />
              {searchQuery && (
                <button onClick={() => dispatch(setSearchQuery(''))}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Dept filter */}
            <div className="w-44">
              <SearchableSelect
                value={deptFilter}
                onChange={setDeptFilter}
                options={departments.map(({ code, label }) => ({ value: code, label }))}
                emptyOption={{ value: '', label: 'All Depts' }}
                searchPlaceholder="Search departments..."
                className={`w-full px-3 py-2 border rounded-xl text-sm transition-colors
                  ${deptFilter ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-white'}`}
              />
            </div>

            {/* Physical Location filter */}
            {/* <div className="relative">
              <button
                onClick={() => setShowLocDD(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-2 border rounded-xl text-sm transition-colors
                  ${locFilter ? 'border-slate-500 bg-slate-50 text-slate-700' : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-white'}`}
              >
                <MapPinned size={12} />
                <span className="max-w-[120px] truncate">
                  {locFilterLoading ? 'Loading...' : locFilter ? phyLocNameFor(locFilter) : 'Physical Location'}
                </span>
                <ChevronDown size={11} className={`transition-transform ${showLocDD ? 'rotate-180' : ''}`} />
              </button>
              {showLocDD && (
                <div className="absolute right-0 mt-1.5 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-20 py-1.5 max-h-72 overflow-y-auto">
                  <button onClick={() => runLocationFilter('')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-gray-500">
                    All Physical Locations
                  </button>
                  {locationMasterState.loading && (
                    <div className="px-3 py-2 text-xs text-gray-400">Loading locations...</div>
                  )}
                  {!locationMasterState.loading && locOptions.length === 0 && (
                    <div className="px-3 py-2 text-xs text-gray-400">No physical locations found</div>
                  )}
                  {locOptions.map(({ value, label }) => (
                    <button key={value} onClick={() => runLocationFilter(value)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50
                        ${locFilter === value ? 'text-slate-700 font-semibold bg-slate-50' : 'text-gray-700'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div> */}

            {/* ── Active / Inactive switch (Employees tab) ── */}
            {tab === 'employees' && (
              <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                <button
                  onClick={() => setStatusView('active')}
                  title="Active employees"
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors
                    ${statusView === 'active'
                      ? 'bg-emerald-600 text-white'
                      : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${statusView === 'active' ? 'bg-white' : 'bg-emerald-500'}`} />
                  Active ({approvedActiveCount})
                </button>
                <button
                  onClick={() => setStatusView('inactive')}
                  title="Inactive employees"
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-l border-gray-200
                    ${statusView === 'inactive'
                      ? 'bg-gray-600 text-white'
                      : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${statusView === 'inactive' ? 'bg-white' : 'bg-gray-400'}`} />
                  Inactive ({approvedInactiveCount})
                </button>
              </div>
            )}

            {/* ── View toggle Card / Table ── */}
            <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
              <button
                onClick={() => setViewMode('card')}
                title="Card View"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors
                  ${viewMode === 'card'
                    ? 'bg-slate-800 text-white'
                    : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <LayoutGrid size={14} />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                title="Table View"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors
                  ${viewMode === 'table'
                    ? 'bg-slate-800 text-white'
                    : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <Table2 size={14} />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>

            {/* Refresh */}
            {/* <button
              onClick={() => { dispatch(clearError()); dispatch(fetchAllEmployees()); }}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 bg-gray-50 hover:bg-white rounded-xl text-sm text-gray-600 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button> */}


            {/* Export to Excel */}
            <button
              onClick={handleExportExcel}
              disabled={loading || !visibleEmployees.length}
              title={visibleEmployees.length
                ? `Export ${visibleEmployees.length} employee${visibleEmployees.length === 1 ? '' : 's'} to Excel`
                : 'Nothing to export'}
              className="flex items-center gap-1.5 px-3 py-2 border border-primary-200 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown size={13} /> Export Excel
            </button>

            {/* Import from Excel */}
            <button
              onClick={() => setShowImport(true)}
              title="Add many employees from a spreadsheet"
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-medium transition-colors"
            >
              <FileSpreadsheet size={13} /> Import Excel
            </button>

            {/* Add Employee */}
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-colors"
            >
              <Plus size={14} /> Add Employee
            </button>
          </div>
        </div>

        {/* ── Tabs: Employees (approved) / Approvals (pending) ── */}
        <div className="flex items-center gap-1 mt-4 -mb-4 border-b border-gray-200">
          <button
            onClick={() => setTab('employees')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors
              ${tab === 'employees'
                ? 'text-gray-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={
              tab === 'employee'
                ? {
                  borderBottomColor: employeeAccent,
                  color: employeeText,
                } 
                : undefined
            }
          >
            <Users size={15} />
            Employees
          </button>
          <button
            onClick={() => setTab('approvals')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors
              ${tab === 'approvals'
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <ShieldCheck size={15} />
            Approvals
            {/* A real notification badge: solid amber with white numerals on both
                tab states. The old version went pale-on-pale when the tab wasn't
                selected, which is exactly when the count needs to be seen. */}
            {pendingCount > 0 && (
              <span
                title={`${pendingCount} employee${pendingCount === 1 ? '' : 's'} awaiting approval`}
                className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full
                  bg-amber-600 px-1.5 text-[11px] font-bold leading-none text-white tabular-nums
                  ring-2 ring-white shadow-sm"
              >
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </button>
        </div>

        {/* Active filters */}
        {(searchQuery || deptFilter || locFilter) && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">Active filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-lg font-medium">
                "{searchQuery}"
                <button onClick={() => dispatch(setSearchQuery(''))}><X size={10} /></button>
              </span>
            )}
            {deptFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-lg font-medium">
                {departments.find(d => d.code === deptFilter)?.label || deptFilter}
                <button onClick={() => setDeptFilter('')}><X size={10} /></button>
              </span>
            )}
            {locFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-lg font-medium">
                {phyLocNameFor(locFilter)}
                <button onClick={() => runLocationFilter('')}><X size={10} /></button>
              </span>
            )}
            <button onClick={() => { dispatch(setSearchQuery('')); setDeptFilter(''); runLocationFilter(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline ml-1">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mx-6 mt-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle size={14} /><span className="flex-1">{error}</span>
          <button onClick={() => dispatch(clearError())} className="text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      )}

      {/* ── Approval error ── */}
      {approveError && (
        <div className="mx-6 mt-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle size={14} /><span className="flex-1">{approveError}</span>
          <button onClick={() => setApproveError(null)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      )}

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {loading && employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-72 gap-3">
            <LoadingSpinner size="lg" />
            <p className="text-gray-500 text-sm">Loading employees...</p>
          </div>
        ) : locFilterLoading ? (
          <div className="flex flex-col items-center justify-center h-72 gap-3">
            <LoadingSpinner size="lg" />
            <p className="text-gray-500 text-sm">Checking physical locations...</p>
          </div>
        ) : visibleEmployees.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-72 gap-3 text-gray-400">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
              <Users size={28} className="opacity-40" />
            </div>
            <p className="text-sm font-semibold text-gray-500">
              {employees.length === 0 ? 'No employees found' : 'No results match your search'}
            </p>
            <p className="text-xs text-gray-400">Try adjusting your search or filters</p>
            {(searchQuery || deptFilter || locFilter) && (
              <button onClick={() => { dispatch(setSearchQuery('')); setDeptFilter(''); runLocationFilter(''); }}
                className="text-xs text-blue-500 hover:underline font-medium">
                Clear all filters
              </button>
            )}
          </div>
        ) : viewMode === 'card' ? (
          /* ── Card Grid ── */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {visibleEmployees.map(emp => (
              <EmployeeCard
                key={emp.HED_EMPLOYEE_ID || emp.p_ced_service_no || Math.random()}
                employee={emp}
                onClick={handleEmployeeClick}
                onAttendanceClick={svcNo => setAttendanceFor(svcNo)}
                onPhysicalLocationClick={svcNo => setPhysicalLocationFor(svcNo)}
                busy={approvingId === (emp.HED_EMPLOYEE_ID || fld(emp, 'HED_SERVICE_NO', 'p_ced_service_no'))}
              />
            ))}
          </div>
        ) : (
          /* ── Table ── */
          <TableView
            employees={visibleEmployees}
            onRowClick={handleEmployeeClick}
            maps={maps}
            approvingId={approvingId}
          />
        )}
      </div>

      {/* ── Detail Loading Overlay ── */}
      {detailLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-600 font-medium">Loading employee details...</p>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {modalOpen && selectedEmployee && (
  <>
    <EmployeeModal
      employee={selectedEmployee}
      isOpen={modalOpen && !editing}
      onClose={() => dispatch(setModalOpen(false))}
      onEdit={() => setEditing(true)}
      onApprove={handleApprove}
      onReject={handleReject}
      approving={approvingId === (selectedEmployee.HED_EMPLOYEE_ID
        || fld(selectedEmployee, 'HED_SERVICE_NO', 'p_ced_service_no'))}
      approvalError={approveError}
    />
    <EmployeeEditModal
      employee={selectedEmployee}
      isOpen={editing}
      onClose={() => setEditing(false)}
      onSave={handleSaveEmployee}
      saving={saving}
      saveError={saveError}
    />
  </>
)}

      {/* ── Add Employee Form ── */}
      {showAddForm && (
        <AddEmployeeForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => dispatch(fetchAllEmployees())}
        />
      )}

      {/* ── Attendance Modal ── */}
      {attendanceFor && (
        <AttendanceModal
          serviceNo={attendanceFor}
          employeeName={(() => {
            const e = employees.find(x =>
              String(x.HED_SERVICE_NO || x.p_ced_service_no || '').trim() === String(attendanceFor).trim()
            );
            return e ? (fld(e, 'HED_REPORT_NAME', 'p_report_name') || `${fld(e, 'HED_FIRST_NAME', 'p_first_name')} ${fld(e, 'HED_LAST_NAME', 'p_last_name')}`.trim()) : '';
          })()}
          isOpen={!!attendanceFor}
          onClose={() => setAttendanceFor(null)}
        />
      )}

      {/* ── Physical Location Modal ── */}
      {physicalLocationFor && (
        <PhysicalLocationModal
          serviceNo={physicalLocationFor}
          employeeName={(() => {
            const e = employees.find(x =>
              String(x.HED_SERVICE_NO || x.p_ced_service_no || '').trim() === String(physicalLocationFor).trim()
            );
            return e ? (fld(e, 'HED_REPORT_NAME', 'p_report_name') || `${fld(e, 'HED_FIRST_NAME', 'p_first_name')} ${fld(e, 'HED_LAST_NAME', 'p_last_name')}`.trim()) : '';
          })()}
          isOpen={!!physicalLocationFor}
          onClose={() => setPhysicalLocationFor(null)}
        />
      )}

      {/* ── Import Employees from Excel ── */}
      <ImportEmployeesModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => dispatch(fetchAllEmployees())}
      />
    </div>
  );
}